import React from 'react'
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { FarmerSalesIcon } from 'core/components/Icons'
import { PrimarySales } from '../../../domain/entities/SalesReport'
import { SectionCard } from './SectionCard'
import { formatMT } from './format'

interface PrimarySalesPanelProps {
  data: PrimarySales
}

export const PrimarySalesPanel: React.FC<PrimarySalesPanelProps> = ({ data }) => {
  const monthlyList = data?.monthly || []
  const chartData = monthlyList.map((m) => ({
    month: m.month,
    volumeMt: m.volumeMt || 0,
    revenueLakhs: m.revenueLakhs || 0,
  }))
  const hasData = chartData.length > 0

  return (
    <SectionCard title="Primary Sales" subtitle="Volume and revenue booked from Samarth to FPOs, month over month">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 items-start">
        {/* Metric summary table */}
        <div className="h-[210px] w-full border border-border rounded-card bg-surface shadow-soft overflow-hidden flex flex-col">
          <div className="overflow-x-auto overflow-y-auto flex-1 text-xs">
            <table className="w-full text-left divide-y divide-border">
              <thead className="bg-bg text-text-secondary text-xs uppercase tracking-wider font-semibold select-none border-b border-border sticky top-0 z-10">
                <tr>
                  <th className="py-3 px-4 bg-bg">Metric</th>
                  {monthlyList.map((m) => (
                    <th key={m.month} className="py-3 px-4 bg-bg">
                      {m.month}
                    </th>
                  ))}
                  <th className="py-3 px-4 font-bold bg-bg">YTD</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-text-primary bg-surface">
                <tr className="hover:bg-bg/50 transition-colors duration-150">
                  <td className="py-3 px-4 font-semibold text-text-secondary">Volume (MT)</td>
                  {monthlyList.map((m) => (
                    <td key={m.month} className="py-3 px-4 font-medium">
                      {formatMT(m.volumeMt || 0)}
                    </td>
                  ))}
                  <td className="py-3 px-4 font-extrabold text-text-primary">{formatMT(data?.ytdVolumeMt || 0)}</td>
                </tr>
                <tr className="hover:bg-bg/50 transition-colors duration-150">
                  <td className="py-3 px-4 font-semibold text-text-secondary">Revenue (₹ Lakhs)</td>
                  {monthlyList.map((m) => (
                    <td key={m.month} className="py-3 px-4 font-medium">
                      {m.revenueLakhs || 0}
                    </td>
                  ))}
                  <td className="py-3 px-4 font-extrabold text-text-primary">
                    {(data?.ytdRevenueLakhs || 0).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Trend visual */}
        <div className="h-[210px] w-full border border-border rounded-card bg-surface p-4 shadow-soft">
          {!hasData ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-bg text-text-secondary">
                <FarmerSalesIcon size={20} className="opacity-60" />
              </div>
              <p className="text-xs font-semibold text-text-primary">No primary sales trends available</p>
              <p className="mt-0.5 text-[11px] text-text-secondary max-w-[260px]">
                Monthly volume and revenue comparisons will appear here once recorded
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--ft-border)" vertical={false} />
                <XAxis
                  dataKey="month"
                  stroke="var(--ft-text-secondary)"
                  fontSize={10}
                  fontWeight="bold"
                  tickLine={false}
                  axisLine={false}
                  dy={10}
                />
                <YAxis
                  yAxisId="volume"
                  stroke="var(--ft-text-secondary)"
                  fontSize={10}
                  fontWeight="bold"
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  yAxisId="revenue"
                  orientation="right"
                  stroke="var(--ft-text-secondary)"
                  fontSize={10}
                  fontWeight="bold"
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--ft-surface)',
                    borderColor: 'var(--ft-border)',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 'bold',
                    color: 'var(--ft-text)',
                    boxShadow: '0 4px 12px var(--ft-shadow)',
                  }}
                  labelStyle={{ color: 'var(--ft-text-secondary)', marginBottom: 4 }}
                />
                <Bar
                  yAxisId="volume"
                  dataKey="volumeMt"
                  name="Volume (MT)"
                  fill="var(--ft-primary)"
                  radius={[4, 4, 0, 0]}
                  barSize={28}
                />
                <Line
                  yAxisId="revenue"
                  type="monotone"
                  dataKey="revenueLakhs"
                  name="Revenue (₹ Lakhs)"
                  stroke="var(--ft-secondary)"
                  strokeWidth={3}
                  dot={{ r: 4, strokeWidth: 2, stroke: 'var(--ft-secondary)', fill: 'var(--ft-surface)' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </SectionCard>
  )
}
